import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, Check, Plus, Save, Trash2 } from 'lucide-react';
import { apiService, JobObjective, PackGroupDTO } from '@/services/api.service';
import { projectMatches } from '@/config/games';
import { loadPackPalette, PalettePickEntry } from '@/components/config/packPalette';
import { SearchPicker, PickerOption } from '@/components/config/SearchPicker';
import { clearConfigListCache } from '@/components/config/configListCache';
import styles from '@/pages/config/LootboxPage.module.scss';

// ============================================================
// Gain d'XP des métiers — behavior pack Jobs (PAS le container)
// ============================================================
// Un fichier par objectif, sous behavior/objectives/<kind>/<nom>.json :
//
//   { "format_version": "1.0.0",
//     "palajobs:objective_break": {
//       "job": "miner", "target": "paladium:amethyst_ore",
//       "icon": "paladium:amethyst_ore", "experience": 18, "level_needed": 5 } }
//
// break et kill portent target = identifiant ; craft et smelt portent target = { "item": … }.
// On réécrit chaque fichier depuis son texte d'origine en ne touchant qu'aux champs édités : tout
// le reste (format_version, clés inconnues) est conservé.
// ============================================================

const KINDS = ['break', 'kill', 'craft', 'smelt'] as const;
type Kind = (typeof KINDS)[number];

const KIND_LABEL: Record<string, string> = {
  break: 'Casser',
  kill: 'Tuer',
  craft: 'Crafter',
  smelt: 'Cuire',
};

/**
 * Un objectif tel qu'édité : les champs du formulaire + le texte d'origine du fichier.
 *
 * `groupId` est porté par la LIGNE et non par la page : les objectifs peuvent être répartis sur
 * plusieurs groupes de packs du projet, et chacun doit être réécrit dans le sien.
 */
interface Row extends JobObjective {
  dirty?: boolean;
  groupId: string;
}

function indentOf(raw: string): number {
  const m = raw.match(/\n(\s+)"/);
  return m ? m[1].length : 2;
}

/** Identité d'une ligne : le chemin seul peut se répéter d'un groupe de packs à l'autre. */
function rowKey(r: Row): string {
  return `${r.groupId}:${r.path}`;
}

/** Réécrit le fichier d'un objectif en préservant tout ce que le formulaire ne gère pas. */
function serialize(row: Row): string | null {
  try {
    const doc = JSON.parse(row.content) as Record<string, unknown>;
    const key = Object.keys(doc).find((k) => k.startsWith('palajobs:objective_'));
    if (!key) return null;
    const o = { ...(doc[key] as Record<string, unknown>) };
    o.job = row.job ?? '';
    // La forme de `target` dépend du type : ne pas la changer casserait la lecture côté moteur.
    if (row.kind === 'craft' || row.kind === 'smelt') {
      const prev = (o.target && typeof o.target === 'object') ? (o.target as Record<string, unknown>) : {};
      o.target = { ...prev, item: row.target ?? '' };
    } else {
      o.target = row.target ?? '';
    }
    if (row.icon) o.icon = row.icon; else delete o.icon;
    if (row.experience !== null && row.experience !== undefined) o.experience = row.experience;
    if (row.levelNeeded !== null && row.levelNeeded !== undefined) o.level_needed = row.levelNeeded;
    doc[key] = o;
    return JSON.stringify(doc, null, indentOf(row.content)) + '\n';
  } catch {
    return null;
  }
}

export function JobsXpTab({ project }: { project: string }) {
  const [groupId, setGroupId] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [jobFilter, setJobFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [palette, setPalette] = useState<PalettePickEntry[]>([]);

  useEffect(() => {
    loadPackPalette(project).then(setPalette).catch(() => setPalette([]));
  }, [project]);

  const options = useMemo<PickerOption[]>(
    () => palette.map((p) => ({
      value: p.id, label: p.id, hint: p.kind === 'block' ? 'block' : 'item',
      thumbnailPath: p.path, groupId: p.groupId,
    })),
    [palette],
  );

  /**
   * Rassemble les objectifs de TOUS les groupes de packs du projet. Aucun nom de pack n'est deviné.
   *
   * S'arrêter au premier groupe non vide (ce que faisait cette fonction) masquait tout le reste :
   * un groupe portant trois objectifs de test gagnait la course et le vrai JobsBehaviorPack, avec
   * ses dizaines d'objectifs et ses autres métiers, n'était jamais lu.
   */
  const load = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    const gr = await apiService.getPacksGroups();
    const groups = (gr.data?.groups ?? []).filter((g: PackGroupDTO) => projectMatches(g.projects, project));

    const perGroup = await Promise.all(groups.map(async (g: PackGroupDTO) => {
      const res = await apiService.getJobObjectives(g.id);
      return (res.data?.objectives ?? []).map((o) => ({ ...o, groupId: g.id } as Row));
    }));

    const all = perGroup.flat();
    // Le groupe d'écriture des nouveaux objectifs : celui qui en porte le plus, donc le vrai pack Jobs.
    const counts = new Map<string, number>();
    for (const r of all) counts.set(r.groupId, (counts.get(r.groupId) ?? 0) + 1);
    const dominant = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

    setGroupId(dominant);
    setRows(all);
    setLoading(false);
  }, [project]);

  useEffect(() => { void load(); }, [load]);

  const jobs = useMemo(
    () => [...new Set(rows.map((r) => r.job).filter((j): j is string => !!j))].sort(),
    [rows],
  );
  const visible = rows.filter(
    (r) => (!jobFilter || r.job === jobFilter) && (!kindFilter || r.kind === kindFilter),
  );
  const dirtyCount = rows.filter((r) => r.dirty).length;
  /** Nombre de groupes de packs qui portent réellement des objectifs. */
  const packCount = useMemo(() => new Set(rows.map((r) => r.groupId)).size, [rows]);

  const mutate = (key: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (rowKey(r) === key ? { ...r, ...patch, dirty: true } : r)));
  };

  const saveAll = async () => {
    const todo = rows.filter((r) => r.dirty);
    if (todo.length === 0) return;
    setSaving(true);
    setStatus(null);
    let ok = 0;
    const errors: string[] = [];
    for (const row of todo) {
      const content = serialize(row);
      if (content == null) { errors.push(`${row.path} : réécriture impossible`); continue; }
      // Chaque objectif est réécrit dans SON groupe : ils ne viennent pas forcément du même pack.
      const res = await apiService.savePackFile(row.groupId, row.path, content);
      if (res.data?.success) {
        ok++;
        setRows((prev) => prev.map((r) => (rowKey(r) === rowKey(row) ? { ...r, content, dirty: false } : r)));
      } else {
        errors.push(`${row.path} : ${res.data?.message ?? res.error ?? 'échec'}`);
      }
    }
    for (const gid of new Set<string>(todo.map((r) => r.groupId))) clearConfigListCache(gid);
    setSaving(false);
    setStatus({
      ok: errors.length === 0,
      msg: `${ok} objectif(s) enregistré(s)${errors.length ? ` — ${errors.join(' ; ')}` : ''}. Publie le pack depuis Déploiement pour l'appliquer.`,
    });
  };

  /** Crée un objectif : un fichier par cible, dans le dossier du type choisi. */
  const create = async () => {
    if (!groupId) return;
    const kind = (window.prompt(`Type d'objectif (${KINDS.join(', ')})`, kindFilter || 'break') ?? '').trim().toLowerCase();
    if (!KINDS.includes(kind as Kind)) { setStatus({ ok: false, msg: 'Type invalide.' }); return; }
    const job = (window.prompt('Métier', jobFilter || jobs[0] || 'miner') ?? '').trim();
    if (!job) return;
    const target = (window.prompt('Cible (identifiant)', 'paladium:') ?? '').trim();
    if (!target) return;
    const slug = target.split(':').pop()!.replace(/[^a-z0-9_]/gi, '').toLowerCase();
    const path = `behavior/objectives/${kind}/${slug}.json`;
    if (rows.some((r) => r.groupId === groupId && r.path === path)) {
      setStatus({ ok: false, msg: `${slug}.json existe déjà.` });
      return;
    }
    const body: Record<string, unknown> = {
      job,
      target: kind === 'craft' || kind === 'smelt' ? { item: target } : target,
      icon: target,
      experience: 1,
      level_needed: 0,
    };
    const content = JSON.stringify({ format_version: '1.0.0', [`palajobs:objective_${kind}`]: body }, null, 2) + '\n';
    setSaving(true);
    const res = await apiService.savePackFile(groupId, path, content);
    setSaving(false);
    if (!res.data?.success) {
      setStatus({ ok: false, msg: res.data?.message ?? res.error ?? 'Création impossible' });
      return;
    }
    setStatus({ ok: true, msg: `${slug}.json créé dans ${kind}. Ajuste l'XP puis enregistre.` });
    await load();
  };

  const remove = async (row: Row) => {
    if (!window.confirm(`Supprimer l'objectif ${row.path} ? Le métier ne donnera plus d'XP sur ${row.target}.`)) return;
    const res = await apiService.deletePackFile(row.groupId, row.path);
    if (res.data?.success) {
      setRows((prev) => prev.filter((r) => rowKey(r) !== rowKey(row)));
      setStatus({ ok: true, msg: `${row.path} supprimé. Publie le pack pour l'appliquer.` });
    } else {
      setStatus({ ok: false, msg: res.data?.message ?? res.error ?? 'Suppression impossible' });
    }
  };

  if (loading) {
    return <div className={styles.detailEmpty}><Loader2 size={20} className={styles.spin} /> Chargement des objectifs…</div>;
  }

  if (!groupId) {
    return (
      <div className={styles.detailEmpty}>
        <AlertTriangle size={22} />
        <p>Aucun objectif trouvé dans les packs de ce projet.</p>
        <p className={styles.muted}>
          Les gains d'XP vivent dans le behavior pack Jobs, sous <code>behavior/objectives/</code>.
          Synchronise le groupe de packs qui porte JobsBehaviorPack depuis Paramètres généraux → Pack.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.boxHead}>
        <label className={styles.field}>
          <span className={styles.label}>Métier</span>
          <select className={styles.input} value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
            <option value="">tous</option>
            {jobs.map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Type</span>
          <select className={styles.input} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <option value="">tous</option>
            {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </label>
        <div className={styles.totals}>
          <span><strong>{visible.length}</strong> objectif(s) affiché(s) sur {rows.length}</span>
          <span><strong>{jobs.length}</strong> métier(s)</span>
          {packCount > 1 && <span>{packCount} packs</span>}
          {dirtyCount > 0 && <span><strong>{dirtyCount}</strong> modifié(s)</span>}
          <button className={styles.secondaryBtn} onClick={() => void load()}><RefreshCw size={13} /> Recharger</button>
          <button className={styles.secondaryBtn} onClick={create} disabled={saving}><Plus size={13} /> Objectif</button>
          <button className={styles.primaryBtn} onClick={saveAll} disabled={saving || dirtyCount === 0}>
            {saving ? <Loader2 size={13} className={styles.spin} /> : <Save size={13} />} Enregistrer
          </button>
        </div>
      </div>

      {status && (
        <div className={status.ok ? styles.ok : styles.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      <div className={styles.tableHeadXp}>
        <span>Type</span>
        <span>Métier</span>
        <span>Cible</span>
        <span>Icône</span>
        <span>XP</span>
        <span>Niveau requis</span>
        <span />
      </div>

      {visible.map((r) => (
        <div key={rowKey(r)} className={styles.rowEdit}>
          <div className={styles.cellsXp}>
            <span className={styles.muted} title={r.path}>{KIND_LABEL[r.kind] ?? r.kind}</span>
            <span>
              <input className={styles.input} value={r.job ?? ''}
                onChange={(e) => mutate(rowKey(r), { job: e.target.value })} />
            </span>
            <span className={styles.picker}>
              <SearchPicker
                groupId={palette[0]?.groupId ?? ''}
                value={r.target ?? ''}
                options={options}
                showThumbnails
                placeholder="paladium:amethyst_ore"
                onChange={(v) => mutate(rowKey(r), { target: v })}
              />
            </span>
            <span className={styles.picker}>
              <SearchPicker
                groupId={palette[0]?.groupId ?? ''}
                value={r.icon ?? ''}
                options={options}
                showThumbnails
                placeholder="(même que la cible)"
                onChange={(v) => mutate(rowKey(r), { icon: v })}
              />
            </span>
            <span>
              <input className={styles.input} type="number" min={0} value={r.experience ?? 0}
                onChange={(e) => mutate(rowKey(r), { experience: Number(e.target.value) })} />
            </span>
            <span>
              <input className={styles.input} type="number" min={0} value={r.levelNeeded ?? 0}
                onChange={(e) => mutate(rowKey(r), { levelNeeded: Number(e.target.value) })} />
            </span>
            <span className={styles.rowActions}>
              {r.dirty && <span className={styles.muted}>modifié</span>}
              <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer l'objectif"
                onClick={() => void remove(r)}>
                <Trash2 size={13} />
              </button>
            </span>
          </div>
        </div>
      ))}

      {visible.length === 0 && <p className={styles.muted}>Aucun objectif pour ces filtres.</p>}
    </>
  );
}
